import "./UsageMeter.css";

interface UsageMeterProps {
  label: string;
  used: number | null;
  limit?: number;
  grace?: number;
  unlimited: boolean;
  /** A short, non-sensitive note when `used` could not be determined —
   * shown instead of a fabricated count (never render `0` for "unknown"). */
  error?: string;
}

/**
 * "Usage against a limit," rendered as a progress indicator and not just a
 * number (per the brief: an operator scans a bar faster than they do
 * arithmetic). Reused for every metered resource — today just nodes, since
 * that is the one resource this deployment counts (server/internal/
 * entitlements only wires a node guard) — so a future environment counter
 * gets the same treatment for free.
 */
export function UsageMeter({ label, used, limit, grace = 0, unlimited, error }: UsageMeterProps) {
  if (unlimited) {
    return (
      <div className="usage-meter">
        <div className="usage-meter__row">
          <span className="usage-meter__label">{label}</span>
          <span className="usage-meter__value">Unlimited</span>
        </div>
      </div>
    );
  }

  if (used === null || limit === undefined) {
    return (
      <div className="usage-meter">
        <div className="usage-meter__row">
          <span className="usage-meter__label">{label}</span>
          <span className="usage-meter__value usage-meter__value--muted">{error ?? "Usage unavailable"}</span>
        </div>
      </div>
    );
  }

  const effective = limit + grace;
  const ratio = effective > 0 ? Math.min(used / effective, 1) : used > 0 ? 1 : 0;
  const overBase = used > limit;
  const atOrOverEffective = used >= effective;
  const tone = atOrOverEffective ? "danger" : overBase ? "warning" : "positive";

  return (
    <div className="usage-meter">
      <div className="usage-meter__row">
        <span className="usage-meter__label">{label}</span>
        <span className="usage-meter__value">
          {used.toLocaleString()} of {limit.toLocaleString()}
          {grace > 0 && <span className="usage-meter__grace"> (plus {grace} grace)</span>}
        </span>
      </div>
      <div
        className="usage-meter__track"
        role="progressbar"
        aria-label={label}
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={effective}
      >
        <div className={`usage-meter__fill usage-meter__fill--${tone}`} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}
